import { SurveyFormBuilderElement, QuestionElement, SectionElement } from '@factorialco/f0-react'
import {
  FormDetailFragment,
  type FormsElementParamsInput,
  UpdateFormMutationVariables,
} from 'generated/resources/graphql'
import { FormsElementInput } from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import { QuestionType, QuestionnaireType, SectionType } from './types'

function toDatasetEnum(value: unknown): 'currency' | 'vendor' | null {
  if (value === 'currency') return 'currency'
  if (value === 'vendor') return 'vendor'
  return null
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function toIntOrNull(value: unknown): number | null {
  return typeof value === 'number' ? Math.round(value) : null
}

function buildMutationParams(
  element: QuestionnaireType['elements'][number]
): FormsElementParamsInput {
  const p = element.params ?? {}
  return {
    locked: element.locked ?? p.locked === true,
    dataset: toDatasetEnum(p.dataset),
    fieldIdentifier: toStringOrNull(p.fieldIdentifier),
    analyzeWithAi: p.analyzeWithAi === true,
    scoreType: toStringOrNull(p.scoreType),
    maxLength: toIntOrNull(p.maxLength),
    optionalComment: toStringOrNull(p.optionalComment),
    allowCreateOption: p.allowCreateOption === true,
  }
}

export const defaultSections = (type: 'effectiveness' | 'satisfaction'): FormsElementInput => {
  const { lableSection, labelScore, scoreType } =
    type === 'effectiveness'
      ? {
          lableSection: i18n.t(
            'trainings.surveys.create.effectiveness.overall_effectiveness.title'
          ),
          labelScore: i18n.t(
            'trainings.surveys.create.effectiveness.overall_effectiveness.content'
          ),
          scoreType: 'courseeffectiveness',
        }
      : {
          lableSection: i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.title'),
          labelScore: i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.content'),
          scoreType: 'coursesatisfaction',
        }
  return {
    label: lableSection,
    description: '',
    type: 'section',
    position: 0,
    mandatory: true,
    params: {
      analyzeWithAi: null,
      scoreType: null,
      maxLength: null,
      optionalComment: null,
      locked: true,
    },
    elements: [
      {
        label: labelScore,
        description: '',
        type: 'score',
        position: 0,
        mandatory: true,
        params: {
          scoreType,
          locked: true,
        },
        options: [
          {
            label: i18n.t('forms.default_rating_scale.very_low'),
            value: '1',
            id: '',
            isCorrect: false,
          },
          { label: i18n.t('forms.default_rating_scale.low'), value: '2', id: '', isCorrect: false },
          {
            label: i18n.t('forms.default_rating_scale.average'),
            value: '3',
            id: '',
            isCorrect: false,
          },
          {
            label: i18n.t('forms.default_rating_scale.high'),
            value: '4',
            id: '',
            isCorrect: false,
          },
          {
            label: i18n.t('forms.default_rating_scale.very_high'),
            value: '5',
            id: '',
            isCorrect: false,
          },
        ],
      },
    ],
  }
}

const mapChoiceOptions = (question: QuestionType) => {
  return (
    question.options?.map((option) => ({
      value: option.id || option.value || '',
      label: option.label,
      correct: option.isCorrect,
    })) ?? []
  )
}

const createQuestionWithOptions = (
  baseProperties: {
    id: string
    title: string
    description: string
    required: boolean
    locked: boolean
  },
  mappedType:
    | 'select'
    | 'multi-select'
    | 'rating'
    | 'dropdown-single'
    | 'dropdown-multi'
    | 'checkbox',
  question: QuestionType,
  formOptions?: FromQuestionnaireToFormOptions
): QuestionElement => {
  if (
    mappedType === 'select' ||
    mappedType === 'multi-select' ||
    mappedType === 'dropdown-single' ||
    mappedType === 'dropdown-multi'
  ) {
    const mappedOptions = mapChoiceOptions(question)

    if (mappedType === 'dropdown-single') {
      const dataset = question.params?.dataset
      if (dataset && typeof dataset === 'string') {
        return {
          ...baseProperties,
          type: 'dropdown-single' as const,
          datasetKey: dataset,
          ...(formOptions?.dropdownSingleShowSearchBox && { showSearchBox: true }),
          ...(question.params?.allowCreateOption && { allowCreate: true }),
        }
      }
      return {
        ...baseProperties,
        type: 'select' as const,
        options: mappedOptions,
      }
    }

    if (mappedType === 'dropdown-multi') {
      const dataset = question.params?.dataset
      if (dataset && typeof dataset === 'string') {
        return {
          ...baseProperties,
          type: 'dropdown-multi' as const,
          datasetKey: dataset,
          ...(formOptions?.dropdownMultiShowSearchBox && { showSearchBox: true }),
        }
      }
      return {
        ...baseProperties,
        type: 'multi-select' as const,
        options: mappedOptions,
      }
    }

    return {
      ...baseProperties,
      type: mappedType,
      options: mappedOptions,
    }
  }

  if (mappedType === 'checkbox') {
    return {
      ...baseProperties,
      type: 'checkbox' as const,
      label: question.options?.[0]?.label ?? '',
    }
  }

  // rating type
  return {
    ...baseProperties,
    type: 'rating' as const,
    options:
      question.options?.map((option) => ({
        value: Number(option.value),
        label: option.label,
      })) ?? [],
  }
}

const createQuestionProperty = (
  question: QuestionType,
  options?: FromQuestionnaireToFormOptions
) => {
  const mappedType = mapQuestionType(question.type, options)
  const baseProperties = {
    id: question.id || uuid(),
    title: question.label,
    description: question.description,
    required: question.mandatory,
    locked: question.locked,
  }

  switch (mappedType) {
    case 'select':
    case 'multi-select':
    case 'rating':
    case 'dropdown-single':
    case 'dropdown-multi':
    case 'checkbox':
      return createQuestionWithOptions(baseProperties, mappedType, question, options)
    case 'text':
      return { ...baseProperties, type: 'text' as const }
    case 'longText':
      return { ...baseProperties, type: 'longText' as const }
    case 'date':
      return { ...baseProperties, type: 'date' as const }
    case 'link':
      return { ...baseProperties, type: 'link' as const }
    case 'numeric':
      return { ...baseProperties, type: 'numeric' as const }
    case 'file':
      return { ...baseProperties, type: 'file' as const }
    default:
      return { ...baseProperties, type: 'text' as const }
  }
}

const createSectionProperty = (
  section: SectionType,
  options?: FromQuestionnaireToFormOptions
): SectionElement => {
  return {
    id: section.id || uuid(),
    title: section.label,
    description: section.description,
    locked: section.locked,
    questions: section.elements.map((element) => createQuestionProperty(element, options)),
  }
}

export type FromQuestionnaireToFormOptions = {
  /** Map dropdownsingle to 'dropdown-single' (e.g. procurement) or 'select' (default). */
  mapDropdownSingleTo?: 'select' | 'dropdown-single'
  /** Map dropdownmulti to 'dropdown-multi' or 'multi-select' (default). */
  mapDropdownMultiTo?: 'multi-select' | 'dropdown-multi'
  /** When true, enables the search box on dropdown-single elements. */
  dropdownSingleShowSearchBox?: boolean
  /** When true, enables the search box on dropdown-multi elements. */
  dropdownMultiShowSearchBox?: boolean
}

const mapQuestionType = (
  type: QuestionType['type'],
  options?: FromQuestionnaireToFormOptions
):
  | 'text'
  | 'select'
  | 'rating'
  | 'date'
  | 'link'
  | 'multi-select'
  | 'numeric'
  | 'longText'
  | 'dropdown-single'
  | 'dropdown-multi'
  | 'file'
  | 'checkbox' => {
  switch (type) {
    case 'text':
      return 'text'
    case 'longtext':
      return 'longText'
    case 'select':
      return 'select'
    case 'multiselect':
      return 'multi-select'
    case 'number':
      return 'numeric'
    case 'rating':
      return 'rating'
    case 'date':
      return 'date'
    case 'url':
      return 'link'
    case 'dropdownsingle':
      return options?.mapDropdownSingleTo === 'dropdown-single' ? 'dropdown-single' : 'select'
    case 'dropdownmulti':
      return options?.mapDropdownMultiTo === 'dropdown-multi' ? 'dropdown-multi' : 'multi-select'
    case 'score':
      return 'rating' // fallback to rating for score
    case 'file':
      return 'file'
    case 'checkbox':
      return 'checkbox'
    default:
      return 'text'
  }
}

const mapRevertQuestionType = (
  type:
    | 'text'
    | 'select'
    | 'rating'
    | 'date'
    | 'link'
    | 'multi-select'
    | 'numeric'
    | 'longText'
    | 'dropdown-single'
    | 'dropdown-multi'
    | 'file'
    | 'checkbox'
): QuestionType['type'] => {
  switch (type) {
    case 'text':
      return 'text'
    case 'select':
      return 'select'
    case 'rating':
      return 'rating'
    case 'date':
      return 'date'
    case 'link':
      return 'url'
    case 'multi-select':
      return 'multiselect'
    case 'numeric':
      return 'number'
    case 'longText':
      return 'longtext'
    case 'dropdown-single':
      return 'dropdownsingle'
    case 'dropdown-multi':
      return 'dropdownmulti'
    case 'file':
      return 'file'
    case 'checkbox':
      return 'checkbox'
    default:
      return 'text'
  }
}

const SectionElementToQuestionnaireElement = (
  section: SectionElement,
  index: number
): SectionType => {
  return {
    id: section.id,
    label: section.title,
    description: section.description ?? '',
    type: 'section',
    position: index,
    mandatory: false,
    locked: section.locked ?? false,
    params: {},
    options: [],
    elements:
      section.questions?.map((question, questionIndex) =>
        QuestionElementToQuestionnaireElement(question, questionIndex)
      ) ?? [],
  }
}

const getQuestionOptions = (question: QuestionElement): QuestionType['options'] => {
  // f0 checkbox exposes its text via `label` instead of `options` — normalize to options format
  if (question.type === 'checkbox' && 'label' in question) {
    return [{ value: '1', label: String(question.label), isCorrect: false }]
  }
  if ('options' in question && question.options) {
    return question.options.map((option) => ({
      value: option.value.toString(),
      label: option.label,
      isCorrect: 'correct' in option ? (option.correct ?? false) : false,
    }))
  }
  return null
}

const QuestionElementToQuestionnaireElement = (
  question: QuestionElement,
  index: number
): QuestionType => {
  const params: QuestionType['params'] = {}

  if ('datasetKey' in question && typeof question.datasetKey === 'string') {
    params.dataset = question.datasetKey
  }
  if ('allowCreate' in question) {
    params.allowCreateOption = !!question.allowCreate
  }

  return {
    id: question.id,
    label: question.title,
    description: question.description ?? '',
    type: mapRevertQuestionType(question.type),
    position: index,
    mandatory: question.required ?? false,
    locked: question.locked ?? false,
    params,
    options: getQuestionOptions(question),
  }
}

export const FromQuestionnaireToForm = (
  elements: QuestionnaireType['elements'],
  options?: FromQuestionnaireToFormOptions
) =>
  elements.map((element) => {
    if (element.type === 'section') {
      return {
        type: 'section' as const,
        section: createSectionProperty(element, options),
      }
    }
    return {
      type: 'question' as const,
      question: createQuestionProperty(element, options),
    }
  })

export const FromFormToQuestionnaire = (
  elements: SurveyFormBuilderElement[]
): QuestionnaireType['elements'] => {
  return elements
    .map((element, index) =>
      element.type === 'section'
        ? SectionElementToQuestionnaireElement(element.section, index)
        : QuestionElementToQuestionnaireElement(element.question, index)
    )
    .flat()
}

const shouldNullifyId = (id: string | undefined): boolean => {
  return !!id && id.startsWith('new-')
}

export const nullifyIdIfNew = (id: string | undefined): string | null | undefined => {
  return shouldNullifyId(id) ? null : id
}

export const FromQuestionaireToMutationVariables = (
  formId: string,
  anonymous: boolean,
  form: QuestionnaireType
): UpdateFormMutationVariables => {
  return {
    id: formId,
    anonymous: anonymous,
    description: form.description,
    title: form.title,
    elements:
      form.elements.map((element) => {
        return {
          id: nullifyIdIfNew(element.id),
          invariantId: element.invariantId,
          label: element.label,
          description: element.description ?? '',
          type: element.type,
          position: element.position,
          mandatory: element.mandatory,
          params: buildMutationParams(element),
          options: element.options
            ? element.options.map((option) => ({
                id: nullifyIdIfNew(option.id),
                value: option.value,
                label: option.label,
                isCorrect: option.isCorrect,
              }))
            : null,
          elements:
            element.type === 'section' && 'elements' in element
              ? element.elements.map((nestedElement) => ({
                  id: nullifyIdIfNew(nestedElement.id),
                  invariantId: nestedElement.invariantId,
                  label: nestedElement.label,
                  description: nestedElement.description,
                  type: nestedElement.type,
                  position: nestedElement.position,
                  mandatory: nestedElement.mandatory,
                  params: buildMutationParams(nestedElement),
                  options: nestedElement.options
                    ? nestedElement.options.map((option) => ({
                        id: nullifyIdIfNew(option.id),
                        value: option.value,
                        label: option.label,
                        isCorrect: option.isCorrect,
                      }))
                    : null,
                }))
              : undefined,
        }
      }) ?? [],
  }
}

const findSectionElements = (
  elements: FormDetailFragment['elements'],
  sectionId: string
): FormDetailFragment['elements'] => {
  return elements.filter((element) => element.sectionId === sectionId)
}

const fragmentQuestionToElement = (
  element: FormDetailFragment['elements'][number],
  elements: FormDetailFragment['elements']
): SectionType => {
  const sectionQuestions = findSectionElements(elements, element.id)
    .map((sectionElement) => fragmentElementToQuestionnaireElement(sectionElement, false))
    .filter((sectionElement): sectionElement is QuestionType => sectionElement !== undefined)
    .sort((a, b) => a.position - b.position)

  return {
    id: element.id,
    invariantId: element.invariantId ?? undefined,
    label: element.label,
    description: element.description ?? '',
    type: 'section',
    position: element.position,
    mandatory: element.mandatory,
    locked: element.params.locked ?? false,
    params: element.params,
    options: element.options,
    elements: sectionQuestions,
  }
}

const fragmentElementToQuestionnaireElement = (
  element: FormDetailFragment['elements'][number],
  rootElements: boolean
): QuestionType | undefined => {
  if (rootElements && element.sectionId) {
    return undefined
  }
  if (element.type === 'section') {
    return undefined
  }
  return {
    id: element.id,
    invariantId: element.invariantId ?? undefined,
    label: element.label,
    description: element.description ?? '',
    type: element.type,
    position: element.position,
    mandatory: element.mandatory,
    locked: element.params.locked ?? false,
    params: element.params,
    options: element.options,
  }
}

export const FromFragmentToQuestionnaire = (
  data: Pick<FormDetailFragment, 'title' | 'description' | 'elements'>
): QuestionnaireType => {
  const elements: QuestionnaireType['elements'] = data.elements
    .map((element) => {
      if (element.type === 'section') {
        return fragmentQuestionToElement(element, data.elements)
      } else {
        return fragmentElementToQuestionnaireElement(element, true)
      }
    })
    .filter((element): element is QuestionnaireType['elements'][number] => element !== undefined)
    .sort((a, b) => a.position - b.position)

  return {
    title: data.title,
    description: data.description ?? '',
    elements,
  }
}
